import {
  Create,
  DateInput,
  NumberInput,
  ReferenceInput,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  minValue,
  required,
  useChoicesContext,
  useDataProvider,
  useInput,
  useNotify,
  useRedirect,
} from 'react-admin';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import {
  ChangeEvent,
  HTMLAttributes,
  Key,
  KeyboardEvent,
  ReactNode,
  SyntheticEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EnterSubmitBlockingSimpleFormContent } from 'src/client/components/forms/form-components';
import { CampaignArchiveButton } from './CampaignArchiveButton';

type CampaignFormMode = 'Create' | 'Update';
const generatedNumberHelperText = {
  Create: 'Leave blank for the next number',
  Update: 'Leave unchanged to keep the current number',
};

const emptyToNull = (value: unknown) => (value === '' ? null : value);

type CampaignReferenceChoice = {
  id: string | number;
  name?: string | null;
  type?: string | null;
};

type CampaignReferenceCreateType = 'AGENCY' | 'BRAND';

const normalizeReferenceLabel = (value: string) => value.trim().toLocaleLowerCase();
const referenceSearchSeparatorPattern = new RegExp('[^\\p{L}\\p{N}]+', 'gu');

const normalizeReferenceSearchText = (value: string) =>
  normalizeReferenceLabel(value).replace(referenceSearchSeparatorPattern, ' ').trim();

const getReferenceChoiceLabel = (choice?: CampaignReferenceChoice | '' | null) =>
  choice && typeof choice === 'object' && typeof choice.name === 'string' ? choice.name : '';

const isCampaignReferenceTypeMatch = (choice: CampaignReferenceChoice, referenceType?: CampaignReferenceCreateType) =>
  !referenceType || choice.type === referenceType;

// Synthetic option id for the "Create new …" row. Module-level so tests and other
// callers can reference the same sentinel without re-deriving it. Any code that
// inspects loaded choices for an exact match (selectedLoadedChoice,
// findExactCampaignReferenceChoice, etc.) MUST skip rows with this id.
export const CAMPAIGN_REFERENCE_CREATE_SENTINEL_ID = '__campaign-reference-create-new__';

const isCampaignReferenceCreateSentinel = (choice?: CampaignReferenceChoice | null) =>
  Boolean(choice) && String(choice!.id) === CAMPAIGN_REFERENCE_CREATE_SENTINEL_ID;

// Gate: only surface the "Create new …" option for typed values that look like a
// real name. Preserves short legitimate names ("3M", "GE", "BD"); rejects whitespace,
// punctuation-only, and pure-digit input.
const campaignReferenceCreateGateLetterPattern = new RegExp('\\p{L}', 'u');
export const passesCampaignReferenceCreateGate = (typedValue: string) => {
  const trimmed = typedValue.trim();
  if (trimmed.length < 2) return false;
  return campaignReferenceCreateGateLetterPattern.test(trimmed);
};

const buildCampaignReferenceCreateOption = (
  typedValue: string,
  referenceType: CampaignReferenceCreateType,
): CampaignReferenceChoice => ({
  id: CAMPAIGN_REFERENCE_CREATE_SENTINEL_ID,
  name: `Create new ${referenceType === 'AGENCY' ? 'agency' : 'brand'}: "${typedValue.trim()}"`,
  type: referenceType,
});

export const findExactCampaignReferenceChoice = (
  choices: CampaignReferenceChoice[] = [],
  typedValue: string,
  referenceType?: CampaignReferenceCreateType,
) => {
  const normalizedTypedValue = normalizeReferenceLabel(typedValue);
  if (!normalizedTypedValue) return undefined;

  return [...choices]
    .filter(
      choice =>
        !isCampaignReferenceCreateSentinel(choice) &&
        isCampaignReferenceTypeMatch(choice, referenceType) &&
        normalizeReferenceLabel(getReferenceChoiceLabel(choice)) === normalizedTypedValue,
    )
    .sort((left, right) => String(left.id).localeCompare(String(right.id)))[0];
};

export const filterCampaignReferenceChoices = (choices: CampaignReferenceChoice[] = [], typedValue: string) => {
  if (!typedValue.trim()) return choices;

  const normalizedTypedValue = normalizeReferenceSearchText(typedValue);
  if (!normalizedTypedValue) return [];

  return choices.filter(choice => {
    const normalizedLabel = normalizeReferenceSearchText(getReferenceChoiceLabel(choice));
    return ` ${normalizedLabel}`.includes(` ${normalizedTypedValue}`);
  });
};

const dedupeCampaignReferenceChoices = (choices: CampaignReferenceChoice[]) => {
  const choicesById = new Map<string, CampaignReferenceChoice>();
  choices.forEach(choice => {
    choicesById.set(String(choice.id), choice);
  });
  return Array.from(choicesById.values());
};

export const campaignReferenceFilterToQuery = (searchText: string, referenceType?: CampaignReferenceCreateType) => {
  const typedValue = searchText.trim();
  return {
    ...(typedValue.length > 0 ? { name: { operator: 'includesInsensitive', value: typedValue } } : {}),
    ...(referenceType ? { type: referenceType } : {}),
  };
};

const campaignReferenceExactFilter = (typedValue: string, referenceType?: CampaignReferenceCreateType) => ({
  name: { operator: 'equalToInsensitive', value: typedValue },
  ...(referenceType ? { type: referenceType } : {}),
});

// Bridges the deferred-resolution autocomplete inputs and the Create/Edit transform.
// When a user types a new Agency/Brand and clicks Save before blur's async resolve
// commits, the form's data clone (taken in react-hook-form's handleSubmit *before*
// validation) still has the old (or null) FK — even though resolveTypedValue is
// about to call field.onChange. We need a side-channel so the async transform can
// (a) wait for any in-flight resolves and (b) overlay the post-resolve FK onto
// the submitted data.
type CampaignReferenceFormCoordinator = {
  registerInflightResolve: (source: string, promise: Promise<unknown>) => void;
  recordCommittedId: (source: string, id: string | number | null) => void;
  awaitInflightResolves: () => Promise<void>;
  consumeCommittedIds: () => Record<string, string | number | null>;
};

const CampaignReferenceFormCoordinatorContext = createContext<CampaignReferenceFormCoordinator | null>(null);

const useNewCampaignReferenceFormCoordinator = (): CampaignReferenceFormCoordinator => {
  const pendingResolvesRef = useRef(new Map<string, Promise<unknown>>());
  const committedIdsRef = useRef(new Map<string, string | number | null>());

  return useMemo<CampaignReferenceFormCoordinator>(
    () => ({
      registerInflightResolve(source, promise) {
        pendingResolvesRef.current.set(source, promise);
        promise.finally(() => {
          if (pendingResolvesRef.current.get(source) === promise) {
            pendingResolvesRef.current.delete(source);
          }
        });
      },
      recordCommittedId(source, id) {
        committedIdsRef.current.set(source, id);
      },
      async awaitInflightResolves() {
        while (pendingResolvesRef.current.size > 0) {
          await Promise.allSettled(Array.from(pendingResolvesRef.current.values()));
        }
      },
      consumeCommittedIds() {
        return Object.fromEntries(committedIdsRef.current.entries());
      },
    }),
    [],
  );
};

export const CampaignReferenceFormCoordinatorProvider = ({ children }: { children: ReactNode }) => {
  const coordinator = useNewCampaignReferenceFormCoordinator();
  return (
    <CampaignReferenceFormCoordinatorContext.Provider value={coordinator}>
      {children}
    </CampaignReferenceFormCoordinatorContext.Provider>
  );
};

export const useCampaignReferenceFormCoordinator = () => useContext(CampaignReferenceFormCoordinatorContext);

export const buildCampaignReferenceTransform = (coordinator: CampaignReferenceFormCoordinator | null) => async (
  data: any,
) => {
  if (coordinator) {
    await coordinator.awaitInflightResolves();
    const overrides = coordinator.consumeCommittedIds();
    return transformCampaign({ ...data, ...overrides });
  }
  return transformCampaign(data);
};

const preventAutocompleteEnterDefault = (event: KeyboardEvent<HTMLDivElement>) => {
  event.preventDefault();
  event.stopPropagation();
  (event as KeyboardEvent<HTMLDivElement> & { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;
};

const shouldRenderCampaignReferenceSuggestions = (filterValue: string) => filterValue.trim().length > 0;

type CampaignReferenceAutocompleteInputProps = {
  createTypeForUnmatched?: CampaignReferenceCreateType;
  fullWidth?: boolean;
  label: string;
  source: string;
};

export const CampaignReferenceAutocompleteInput = ({
  createTypeForUnmatched,
  fullWidth = true,
  label,
  source,
}: CampaignReferenceAutocompleteInputProps) => {
  const dataProvider = useDataProvider();
  // `resource` is the reference resource of the enclosing <ReferenceInput> (e.g. 'advertisers').
  // Read it from context so the server-side exact-match lookup targets the same endpoint the
  // ReferenceInput loads choices from, rather than a hardcoded resource. The exact-match filter
  // and optionText still assume the reference exposes a `name` field.
  const { allChoices, isFetching: isReferenceFetching, resource } = useChoicesContext<CampaignReferenceChoice>();
  const { field } = useInput({ source });
  // Optional — when null, the autocomplete still works but the Save-mid-resolve race
  // is not blocked (the legacy behavior). The Campaign Create/Edit screens wrap the
  // form in CampaignReferenceFormCoordinatorProvider to opt in.
  const coordinator = useCampaignReferenceFormCoordinator();
  const typedValueRef = useRef('');
  const hasUnresolvedTypedValueRef = useRef(false);
  const lookupVersionRef = useRef(0);
  const suggestionQueryVersionRef = useRef(0);
  const [typedValue, setTypedValue] = useState('');
  const [committedChoice, setCommittedChoice] = useState<CampaignReferenceChoice | undefined>();
  const [queriedChoices, setQueriedChoices] = useState<CampaignReferenceChoice[]>([]);
  const [isSuggestionFetching, setIsSuggestionFetching] = useState(false);

  const loadedChoices = useMemo(
    () =>
      dedupeCampaignReferenceChoices([
        ...queriedChoices,
        ...allChoices.filter(choice => isCampaignReferenceTypeMatch(choice, createTypeForUnmatched)),
        ...(committedChoice ? [committedChoice] : []),
      ]),
    [allChoices, committedChoice, createTypeForUnmatched, queriedChoices],
  );

  const visibleChoices = useMemo(() => {
    const trimmed = typedValue.trim();
    if (!trimmed) return [];
    const matches = filterCampaignReferenceChoices(loadedChoices, typedValue);
    if (!createTypeForUnmatched) return matches;
    if (!passesCampaignReferenceCreateGate(trimmed)) return matches;
    if (findExactCampaignReferenceChoice(loadedChoices, trimmed, createTypeForUnmatched)) return matches;
    return [...matches, buildCampaignReferenceCreateOption(trimmed, createTypeForUnmatched)];
  }, [createTypeForUnmatched, loadedChoices, typedValue]);
  const selectedLoadedChoice = useMemo(
    () =>
      loadedChoices.find(
        choice => !isCampaignReferenceCreateSentinel(choice) && String(choice.id) === String(field.value),
      ),
    [field.value, loadedChoices],
  );
  const selectedChoice =
    selectedLoadedChoice ??
    (committedChoice && String(committedChoice.id) === String(field.value) ? committedChoice : null);

  useEffect(() => {
    if (selectedLoadedChoice) {
      setCommittedChoice(selectedLoadedChoice);
    } else if (!field.value) {
      setCommittedChoice(undefined);
    }
  }, [field.value, selectedLoadedChoice]);

  useEffect(() => {
    if (hasUnresolvedTypedValueRef.current) return;

    const choiceLabel = getReferenceChoiceLabel(selectedChoice);
    typedValueRef.current = choiceLabel;
    setTypedValue(choiceLabel);
  }, [selectedChoice]);

  const findLoadedExactChoice = useCallback(
    (value: string) => findExactCampaignReferenceChoice(loadedChoices, value, createTypeForUnmatched),
    [createTypeForUnmatched, loadedChoices],
  );

  const commitChoice = useCallback(
    (choice: CampaignReferenceChoice) => {
      const choiceLabel = getReferenceChoiceLabel(choice);
      setCommittedChoice(choice);
      typedValueRef.current = choiceLabel;
      hasUnresolvedTypedValueRef.current = false;
      setTypedValue(choiceLabel);
      setQueriedChoices([choice]);
      field.onChange(choice.id, choice);
      coordinator?.recordCommittedId(source, choice.id);
    },
    [coordinator, field, source],
  );

  const clearSelection = useCallback(() => {
    setCommittedChoice(undefined);
    setQueriedChoices([]);
    field.onChange('', '');
    coordinator?.recordCommittedId(source, null);
  }, [coordinator, field, source]);

  const loadMatchingChoices = useCallback(
    async (nextInputValue: string) => {
      const queryValue = nextInputValue.trim();
      const queryVersion = ++suggestionQueryVersionRef.current;

      if (!queryValue) {
        setIsSuggestionFetching(false);
        setQueriedChoices([]);
        return;
      }

      setIsSuggestionFetching(true);

      try {
        const result = await dataProvider.getList<CampaignReferenceChoice>(resource, {
          filter: campaignReferenceFilterToQuery(queryValue, createTypeForUnmatched),
          pagination: { page: 1, perPage: 25 },
          sort: { field: 'name', order: 'ASC' },
        });

        if (queryVersion === suggestionQueryVersionRef.current) {
          setQueriedChoices(filterCampaignReferenceChoices(result.data, queryValue));
        }
      } catch {
        if (queryVersion === suggestionQueryVersionRef.current) {
          setQueriedChoices([]);
        }
      } finally {
        if (queryVersion === suggestionQueryVersionRef.current) {
          setIsSuggestionFetching(false);
        }
      }
    },
    [createTypeForUnmatched, dataProvider, resource],
  );

  const resolveExactChoice = useCallback(
    async (value: string) => {
      const result = await dataProvider.getList<CampaignReferenceChoice>(resource, {
        filter: campaignReferenceExactFilter(value, createTypeForUnmatched),
        pagination: { page: 1, perPage: 25 },
        sort: { field: 'name', order: 'ASC' },
      });

      return findExactCampaignReferenceChoice(result.data, value, createTypeForUnmatched);
    },
    [createTypeForUnmatched, dataProvider, resource],
  );

  const createUnmatchedChoice = useCallback(
    async (value: string) => {
      if (!createTypeForUnmatched) return undefined;

      const result = await dataProvider.create<CampaignReferenceChoice>(resource, {
        data: {
          name: value,
          type: createTypeForUnmatched,
        },
      });

      return result.data;
    },
    [createTypeForUnmatched, dataProvider, resource],
  );

  const resolveTypedValue = useCallback(
    async (rawValue: string) => {
      const value = rawValue.trim();
      const lookupVersion = ++lookupVersionRef.current;

      if (!value) {
        typedValueRef.current = '';
        hasUnresolvedTypedValueRef.current = false;
        setTypedValue('');
        clearSelection();
        return;
      }

      const currentlySelected = loadedChoices.find(choice => String(choice.id) === String(field.value));
      if (normalizeReferenceLabel(getReferenceChoiceLabel(currentlySelected)) === normalizeReferenceLabel(value)) {
        hasUnresolvedTypedValueRef.current = false;
        return;
      }

      const loadedExactChoice = findLoadedExactChoice(value);
      if (loadedExactChoice) {
        commitChoice(loadedExactChoice);
        return;
      }

      // Don't clear the field before the server lookup resolves: a save-mid-await would
      // otherwise persist null even though the exact-match query is about to succeed.
      let exactChoice: CampaignReferenceChoice | undefined;
      try {
        exactChoice = await resolveExactChoice(value);
      } catch {
        exactChoice = undefined;
      }

      // Bail if the typed value has since changed or another resolve has superseded
      // this one — preserves the user's latest input as the source of truth.
      if (
        lookupVersion !== lookupVersionRef.current ||
        normalizeReferenceLabel(typedValueRef.current) !== normalizeReferenceLabel(value)
      ) {
        return;
      }

      try {
        if (exactChoice) {
          commitChoice(exactChoice);
          return;
        }
        // No exact match and no create from this path. Revert the visible text to the
        // last committed choice; the explicit "Create new …" menu item is the only
        // create entry point.
        const fallbackLabel = getReferenceChoiceLabel(selectedChoice);
        typedValueRef.current = fallbackLabel;
        setTypedValue(fallbackLabel);
      } finally {
        if (lookupVersion === lookupVersionRef.current) {
          hasUnresolvedTypedValueRef.current = false;
        }
      }
    },
    [
      clearSelection,
      commitChoice,
      field.value,
      findLoadedExactChoice,
      loadedChoices,
      resolveExactChoice,
      selectedChoice,
    ],
  );

  const applyTypedInputValue = useCallback(
    (nextInputValue: string) => {
      if (hasUnresolvedTypedValueRef.current && typedValueRef.current === nextInputValue) {
        setTypedValue(nextInputValue);
        return;
      }

      typedValueRef.current = nextInputValue;
      hasUnresolvedTypedValueRef.current = true;
      setTypedValue(nextInputValue);
      void loadMatchingChoices(nextInputValue);
    },
    [loadMatchingChoices],
  );

  const handleInputChange = useCallback(
    (_event: SyntheticEvent<Element, Event> | null, nextInputValue: string, reason: string) => {
      if (reason === 'input') {
        applyTypedInputValue(nextInputValue);
        return;
      }

      if (reason === 'clear') {
        applyTypedInputValue('');
      }
    },
    [applyTypedInputValue],
  );

  const handleTextInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      applyTypedInputValue(event.target.value);
    },
    [applyTypedInputValue],
  );

  const handleChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, record: CampaignReferenceChoice | null) => {
      if (record && isCampaignReferenceCreateSentinel(record)) {
        if (!createTypeForUnmatched) return;
        const valueToCreate = typedValueRef.current.trim();
        if (!passesCampaignReferenceCreateGate(valueToCreate)) return;

        // Defensive: another fast lookup may have just landed an exact match.
        const existing = findLoadedExactChoice(valueToCreate);
        if (existing) {
          commitChoice(existing);
          return;
        }

        // Mark as resolved synchronously so a subsequent blur (e.g. from a Save click
        // moving focus) does NOT call resolveTypedValue and overwrite this create's
        // coordinator entry under the same source key. The user clicking the explicit
        // create option is a commitment — no further async resolution should fire.
        hasUnresolvedTypedValueRef.current = false;

        const pending = (async () => {
          try {
            // Server-side exact-match recheck: the `loadMatchingChoices` background
            // includes lookup may not have populated `queriedChoices` yet, so the
            // synthetic option can race with an existing row. Reuse it to avoid
            // duplicate POSTs to /advertisers.
            const exactBeforeCreate = await resolveExactChoice(valueToCreate);

            // Bail before the create POST if the user has retyped during the exact-match
            // lookup. Without this guard, createUnmatchedChoice would still POST and
            // insert an orphan advertiser row (the post-POST commit guard would correctly
            // skip binding it, but the row would already exist).
            if (
              normalizeReferenceLabel(typedValueRef.current) !== normalizeReferenceLabel(valueToCreate)
            ) {
              return;
            }

            if (exactBeforeCreate) {
              commitChoice(exactBeforeCreate);
              return;
            }
            const created = await createUnmatchedChoice(valueToCreate);
            if (
              created &&
              normalizeReferenceLabel(typedValueRef.current) === normalizeReferenceLabel(valueToCreate)
            ) {
              commitChoice(created);
            }
          } catch {
            // Create (or exact recheck) failed — restore the unresolved sentinel and
            // revert the visible text to the last committed choice so the user can
            // retry without a confusing half-committed state.
            hasUnresolvedTypedValueRef.current = true;
            const fallback = getReferenceChoiceLabel(selectedChoice);
            typedValueRef.current = fallback;
            setTypedValue(fallback);
          }
        })();
        coordinator?.registerInflightResolve(source, pending);
        return;
      }

      if (record) {
        commitChoice(record);
        return;
      }

      typedValueRef.current = '';
      hasUnresolvedTypedValueRef.current = false;
      setTypedValue('');
      clearSelection();
    },
    [
      clearSelection,
      commitChoice,
      coordinator,
      createTypeForUnmatched,
      createUnmatchedChoice,
      findLoadedExactChoice,
      resolveExactChoice,
      selectedChoice,
      source,
    ],
  );

  const handleBlur = useCallback(() => {
    field.onBlur();
    if (!hasUnresolvedTypedValueRef.current) return;

    const value = typedValueRef.current.trim();
    if (!value) {
      typedValueRef.current = '';
      hasUnresolvedTypedValueRef.current = false;
      setTypedValue('');
      clearSelection();
      return;
    }

    const pending = resolveTypedValue(value);
    coordinator?.registerInflightResolve(source, pending);
  }, [clearSelection, coordinator, field, resolveTypedValue, source]);

  const highlightedOptionRef = useRef<CampaignReferenceChoice | null>(null);

  const handleHighlightChange = useCallback(
    (_event: SyntheticEvent | null, option: CampaignReferenceChoice | null, _reason: string) => {
      highlightedOptionRef.current = option;
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter') return;

      // If the user hasn't touched the field (e.g. Edit page just opened with a loaded
      // value), Enter must not be treated as a clear — preserve the existing FK and
      // suppress the default form submit.
      if (!hasUnresolvedTypedValueRef.current) {
        preventAutocompleteEnterDefault(event);
        return;
      }

      // If MUI has a highlighted option (including the synthetic "Create new …"),
      // let it dispatch its onChange so handleChange routes the selection. Calling
      // preventAutocompleteEnterDefault here would short-circuit MUI's
      // highlighted-option-selection branch and the create would never fire.
      if (highlightedOptionRef.current) return;

      const value = typedValueRef.current.trim();
      const exactChoice = findLoadedExactChoice(value);

      preventAutocompleteEnterDefault(event);

      if (exactChoice) {
        commitChoice(exactChoice);
        return;
      }

      const pending = resolveTypedValue(value);
      coordinator?.registerInflightResolve(source, pending);
    },
    [commitChoice, coordinator, findLoadedExactChoice, resolveTypedValue, source],
  );

  const autocompleteValue =
    selectedChoice && (!typedValue.trim() || filterCampaignReferenceChoices([selectedChoice], typedValue).length > 0)
      ? selectedChoice
      : null;

  return (
    <Autocomplete
      id={source}
      value={autocompleteValue}
      inputValue={typedValue}
      options={shouldRenderCampaignReferenceSuggestions(typedValue) ? visibleChoices : []}
      getOptionLabel={getReferenceChoiceLabel}
      isOptionEqualToValue={(option, value) => {
        if (isCampaignReferenceCreateSentinel(option) || isCampaignReferenceCreateSentinel(value)) return false;
        return String(option.id) === String(value.id);
      }}
      filterOptions={options => options}
      loading={isReferenceFetching || isSuggestionFetching}
      fullWidth={fullWidth}
      clearOnBlur={false}
      noOptionsText={
        typedValue.trim()
          ? `No matching ${String(label).toLocaleLowerCase()} found`
          : `Start typing to search ${String(label).toLocaleLowerCase()}`
      }
      autoHighlight={Boolean(findLoadedExactChoice(typedValue))}
      openOnFocus
      selectOnFocus
      onBlur={handleBlur}
      onChange={handleChange}
      onHighlightChange={handleHighlightChange}
      onInputChange={handleInputChange}
      renderOption={(props, option) => {
        const optionLabel = getReferenceChoiceLabel(option);
        // MUI's getOptionProps returns `key` inside the spread object. React 18
        // emits a deprecation warning (and React 19 errors) if we spread the
        // props and then also write an explicit `key={…}` — destructure it.
        const { key, ...optionProps } = props as HTMLAttributes<HTMLLIElement> & { key: Key };
        if (isCampaignReferenceCreateSentinel(option)) {
          return (
            <li key={key} {...optionProps} data-testid="campaign-reference-create-option">
              <em>{optionLabel}</em>
            </li>
          );
        }
        return (
          <li key={key} {...optionProps}>
            {optionLabel}
          </li>
        );
      }}
      onKeyDown={handleKeyDown}
      renderInput={params => (
        <TextField
          {...params}
          name={field.name}
          inputRef={field.ref}
          label={label}
          margin="dense"
          variant="filled"
          onChange={handleTextInputChange}
        />
      )}
    />
  );
};

export const removeEmptyGeneratedNumber = <T extends Record<string, any>>(data: T, key: keyof T): T => {
  if (data[key] !== null && data[key] !== undefined && data[key] !== '') return data;

  const nextData = { ...data };
  delete nextData[key];
  return nextData;
};

export const validateOptionalUrl = (value?: string) => {
  if (!value) return undefined;

  try {
    new URL(value);
    return undefined;
  } catch {
    return 'Must be a valid URL';
  }
};

export const transformCampaign = (data: any) => ({
  ...removeEmptyGeneratedNumber(data, 'campaignNumber'),
  advertiserId: emptyToNull(data.advertiserId),
  agencyId: emptyToNull(data.agencyId),
  brandId: emptyToNull(data.brandId),
  billToAdvertiserId: emptyToNull(data.billToAdvertiserId),
  budget: emptyToNull(data.budget),
  dcmAdvertiserId: emptyToNull(data.dcmAdvertiserId),
  gamOrderId: emptyToNull(data.gamOrderId),
  sharepointContractUrl: emptyToNull(data.sharepointContractUrl),
});

const CampaignFormToolbar = ({ mode }: { mode: CampaignFormMode }) => {
  const redirect = useRedirect();

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Button variant="text" color="inherit" startIcon={<CloseIcon />} onClick={() => redirect('list', 'campaigns')}>
        Cancel
      </Button>
      <Box display="flex" gap={1}>
        <SaveButton label={mode === 'Create' ? 'Save campaign' : 'Save changes'} />
        {mode === 'Update' ? <CampaignArchiveButton /> : null}
      </Box>
    </Toolbar>
  );
};

export const CampaignForm = ({ mode }: { mode: CampaignFormMode }) => (
  <SimpleForm
    component={EnterSubmitBlockingSimpleFormContent}
    toolbar={<CampaignFormToolbar mode={mode} />}
    mode="onBlur"
    reValidateMode="onBlur"
    sx={{ maxWidth: 960 }}
  >
    <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={2}>
      <TextInput source="name" label="Campaign Name" fullWidth resettable validate={required()} />
      <NumberInput
        source="campaignNumber"
        label="Campaign Number"
        fullWidth
        min={1}
        validate={minValue(1)}
        helperText={generatedNumberHelperText[mode]}
      />

      <ReferenceInput source="advertiserId" reference="advertisers" sort={{ field: 'name', order: 'ASC' }}>
        <CampaignReferenceAutocompleteInput source="advertiserId" label="Advertiser" fullWidth />
      </ReferenceInput>
      <ReferenceInput
        source="agencyId"
        reference="advertisers"
        filter={{ type: 'AGENCY' }}
        sort={{ field: 'name', order: 'ASC' }}
      >
        <CampaignReferenceAutocompleteInput
          source="agencyId"
          label="Agency"
          fullWidth
          createTypeForUnmatched="AGENCY"
        />
      </ReferenceInput>
      <ReferenceInput
        source="brandId"
        reference="advertisers"
        filter={{ type: 'BRAND' }}
        sort={{ field: 'name', order: 'ASC' }}
      >
        <CampaignReferenceAutocompleteInput source="brandId" label="Brand" fullWidth createTypeForUnmatched="BRAND" />
      </ReferenceInput>
      <ReferenceInput source="billToAdvertiserId" reference="advertisers" sort={{ field: 'name', order: 'ASC' }}>
        <CampaignReferenceAutocompleteInput source="billToAdvertiserId" label="Bill-To Advertiser" fullWidth />
      </ReferenceInput>

      <DateInput source="startDate" label="Start Date" fullWidth />
      <DateInput source="endDate" label="End Date" fullWidth />

      <TextInput source="sharepointContractUrl" label="SharePoint URL" fullWidth validate={validateOptionalUrl} />
      <TextInput source="poNumber" label="PO Number" fullWidth resettable />

      <TextInput source="gamOrderId" label="GAM Order ID" fullWidth resettable />
      <TextInput source="dcmAdvertiserId" label="DCM Advertiser ID" fullWidth resettable />

      <TextInput source="billingSchedule" label="Billing Schedule" fullWidth resettable />
      <TextInput source="billingPeriod" label="Billing Period" fullWidth resettable />

      <TextInput source="traffickerEmail" label="Trafficker Email" fullWidth resettable />
      <TextInput source="salespersonEmail" label="Salesperson Email" fullWidth resettable />

      <NumberInput source="budget" label="Total Contract Value" fullWidth />
    </Box>
    <TextInput source="description" label="Description" fullWidth resettable multiline minRows={3} />
  </SimpleForm>
);

const CampaignCreateInner = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const coordinator = useCampaignReferenceFormCoordinator();
  const transform = useMemo(() => buildCampaignReferenceTransform(coordinator), [coordinator]);

  return (
    <Create
      transform={transform}
      mutationOptions={{
        onSuccess: (data: any) => {
          notify('Saved campaign');
          redirect('show', 'campaigns', data.id);
        },
      }}
    >
      <CampaignForm mode="Create" />
    </Create>
  );
};

export const CampaignCreate = () => (
  <CampaignReferenceFormCoordinatorProvider>
    <CampaignCreateInner />
  </CampaignReferenceFormCoordinatorProvider>
);
